<template>
  <content-popup
    v-click-outside:#app.stop.prevent.capture="handleClickOutside"
    class="resource-prerequisite-popup"
    :title="title"
    :visible="visible"
    :primary-button-label="t('button_ok')"
    :secondary-button-label="t('button_cancel')"
    :reference-element="referenceElement"
    :show-close-button="false"
    :placement="placement"
    @on-confirm="handleConfirm"
    @on-cancel="handleClose"
  >
    <template #content>
      <div class="info">
        {{ subtitle }}

        <i18n-t v-if="isGameContext" keypath="gamification_game_condition_user_guide_label">
          <template #goToUserGuide>
            <cv-link :href="userGuideUrl" target="_blank">
              {{ t("gamification_game_condition_user_guide_here_label") }}
            </cv-link>
          </template>
        </i18n-t>

        <cv-inline-notification v-else kind="info" low-contrast hide-close-button>
          <template #subtitle>
            <div>{{ t("add_resource_prerequisite_inline_notification_info_label") }}</div>
          </template>
        </cv-inline-notification>
      </div>

      <cv-form v-if="true" @submit.prevent>
        <dl class="field-set resource-prerequisite-list">
          <dd class="field-input">
            <template
              v-for="(selectedPrerequisite, index) in selectedPrerequisiteList"
              :key="`prerequisite-${index}`"
            >
              <!--
                1) resource-prerequisite-item: The container for the dropdowns
                2) prerequisite-selection: The radio buttons for the OR/AND selection
              -->
              <div class="resource-prerequisite-item-wrapper">
                <!-- Display of Lesson Progress - Section, Activity and criterion -->
                <!-- Used in both lesson plan and gamification conditions -->
                <div
                  v-if="isActivityPrerequisiteType(selectedPrerequisite)"
                  class="resource-prerequisite-item"
                >
                  <div
                    class="section-prerequisite-dropdown-container"
                    @click="anchorToCurrentSectionDropdownItem(index)"
                  >
                    <dropdown-component
                      :class="`section-prerequisite-dropdown-${index} prerequisite-dropdown`"
                      :placeholder="t('drop_down_item_select_selection_label')"
                      :invalid-message="sectionErrorMessage(selectedPrerequisite)"
                      :value="selectedPrerequisite.sectionId?.toString()"
                      @change="handleChangeSectionPrerequisite(index, $event)"
                    >
                      <template
                        v-if="sectionPrerequisitesSelectedObject(selectedPrerequisite)"
                        #internal-caption
                      >
                        <label
                          class="section-label"
                          :data-icon-text="
                            sectionIconText(
                              sectionPrerequisitesSelectedObject(selectedPrerequisite)?.pageNo,
                            )
                          "
                        >
                          <SectionIcon />
                        </label>

                        <ellipsis-text
                          :text="sectionPrerequisitesSelectedObject(selectedPrerequisite)?.title"
                        />
                      </template>

                      <cv-dropdown-item
                        v-for="sectionPrerequisite in filteredPrerequisitesDropDownData"
                        :key="sectionPrerequisite.id"
                        :value="sectionPrerequisite.id.toString()"
                      >
                        <label
                          class="section-label"
                          :data-icon-text="sectionIconText(sectionPrerequisite.pageNo)"
                        >
                          <SectionIcon />
                        </label>

                        <ellipsis-text :text="sectionPrerequisite.title" />
                      </cv-dropdown-item>
                    </dropdown-component>
                  </div>

                  <dropdown-component
                    class="activity-prerequisite-dropdown prerequisite-dropdown"
                    :placeholder="t('drop_down_item_select_activity_quiz_label')"
                    :disabled="selectedPrerequisite.sectionId == null"
                    :invalid-message="activityErrorMessage(selectedPrerequisite)"
                    :value="selectedActivity(selectedPrerequisite.activityId)"
                    @change="
                      handleChangeActivityPrerequisite(
                        index,
                        selectedPrerequisite.sectionId,
                        $event,
                      )
                    "
                  >
                    <template
                      v-if="activityPrerequisitesSelectedObject(selectedPrerequisite)"
                      #internal-caption
                    >
                      <label
                        class="activity-label"
                        :data-icon-text="
                          activityPrerequisitesSelectedObject(selectedPrerequisite)?.pageNo
                        "
                      >
                        <component
                          :is="
                            activityLabelIcon(
                              activityPrerequisitesSelectedObject(selectedPrerequisite)
                                ?.type as ActivityType,
                            )
                          "
                        />
                      </label>

                      <ellipsis-text
                        :text="activityPrerequisitesSelectedObject(selectedPrerequisite)?.title"
                      />
                    </template>

                    <cv-dropdown-item
                      v-for="activityPrerequisite in filteredSectionActivityList(
                        selectedPrerequisite.sectionId,
                      )"
                      :key="activityPrerequisite.id"
                      :value="activityPrerequisite.id.toString()"
                    >
                      <label class="activity-label" :data-icon-text="activityPrerequisite.pageNo">
                        <component :is="activityLabelIcon(activityPrerequisite.type)" />
                      </label>

                      <ellipsis-text :text="activityPrerequisite.title" />
                    </cv-dropdown-item>
                  </dropdown-component>

                  <div class="criterion-selection">
                    <dropdown-component
                      v-model="selectedPrerequisite.criterion"
                      class="criterion-dropdown"
                      :placeholder="t('completion_camel_case')"
                      :disabled="selectedPrerequisite.sectionId == null"
                      @change="
                        (value) => handleChangeCriterionPrerequisite(index, value as CriterionValue)
                      "
                    >
                      <cv-dropdown-item v-if="isGameContext" value="RESOURCE_START">
                        <ellipsis-text :text="t('start_camel_case')" />
                      </cv-dropdown-item>

                      <cv-dropdown-item value="RESOURCE_COMPLETION">
                        <ellipsis-text :text="t('completion_camel_case')" />
                      </cv-dropdown-item>

                      <cv-dropdown-item
                        v-if="
                          activityQuizSelected(
                            selectedPrerequisite.sectionId,
                            selectedPrerequisite.activityId,
                          )
                        "
                        :value="quizScoreDropdownValue"
                      >
                        <ellipsis-text :text="t('quiz_score_camel_case')" />
                      </cv-dropdown-item>
                    </dropdown-component>

                    <template v-if="selectedPrerequisite.criterion === quizScoreDropdownValue">
                      <div>{{ t("is_between_label") }}</div>
                      <cv-number-input
                        min="0"
                        max="100"
                        :model-value="selectedPrerequisite.quizScoreFrom"
                        @keydown.enter.prevent
                        @change="handleChangeQuizScoreFrom(index, $event)"
                        @input="handleInputQuizScoreFrom(index, $event)"
                      />
                      <div>{{ t("and_small_letter_case") }}</div>
                      <cv-number-input
                        min="1"
                        max="100"
                        :model-value="selectedPrerequisite.quizScoreTo"
                        @keydown.enter.prevent
                        @change="handleChangeQuizScoreTo(index, $event)"
                        @input="handleInputQuizScoreTo(index, $event)"
                      />
                    </template>
                  </div>

                  <cv-button kind="ghost" class="icon-button btn-delete-prerequisite">
                    <svg-icon
                      name="Trash24"
                      kind="action"
                      class="delete-prerequisite"
                      @click="deletePrerequisite(index)"
                    />
                  </cv-button>
                </div>

                <!-- Display of Lesson Start, Game Story, Collectibles and Game Level -->
                <!-- Used in gamification conditions only -->
                <ul v-else>
                  <gamification-condition-item
                    :game-data="gameData"
                    :target-game-award="gameAward"
                    :is-from-game-story="isFromGameStory"
                    :hide-section-activity-number="hideSectionActivityNumber"
                    :condition="selectedPrerequisite"
                    :validation-errors="selectedPrerequisite.errorMap"
                    :sections="filteredPrerequisitesDropDownData"
                    @change="
                      handleChangeGamificationConditionItem(
                        index,
                        selectedPrerequisite.criterion,
                        $event,
                      )
                    "
                    @delete="deletePrerequisite(index)"
                  />
                </ul>
              </div>

              <cv-radio-group
                v-if="index !== selectedPrerequisiteList.length - 1"
                :key="index"
                class="prerequisite-selection"
                :vertical="false"
                :class="{
                  'and-selected': selectedOperatorList[index] === 'AND',
                  'or-selected': selectedOperatorList[index] === 'OR',
                }"
                legend-text=""
              >
                <input-radio
                  :id="'and-selection-id-' + index"
                  :key="'prerequisite-and-selection-' + index"
                  value="AND"
                  kind="white"
                  :selected-value="selectedOperatorList[index]"
                  @change="(value) => handleChangeOperator(index, value as Operator)"
                >
                  {{ t("and_camel_case") }}
                </input-radio>
                <input-radio
                  :id="'or-selection-id-' + index"
                  :key="'prerequisite-or-selection-' + index"
                  value="OR"
                  kind="white"
                  :selected-value="selectedOperatorList[index]"
                  @change="(value) => handleChangeOperator(index, value as Operator)"
                >
                  {{ t("or_camel_case") }}
                </input-radio>
              </cv-radio-group>
            </template>
          </dd>
        </dl>
      </cv-form>

      <overflow-menu-button
        up
        size="field"
        kind="tertiary"
        :disabled="addButtonDisabled"
        @click.stop.prevent
      >
        <svg-icon name="Plus24" kind="action" />
        {{ addPrerequisiteButtonLabel }}
        <template #overflow-menu>
          <OverflowMenuItem
            v-if="isGameContext"
            :disabled="hasConditionsPresent"
            @click="addLessonStartCondition"
          >
            {{ t("gamification_game_condition_lesson_starts_label") }}
          </OverflowMenuItem>
          <OverflowMenuItem @click="addLessonProgressCondition">
            {{ t("gamification_game_condition_lesson_progress_label") }}
          </OverflowMenuItem>
          <OverflowMenuItem :disabled="isGameDisabled" @click="addGameStoryCondition">
            {{ t("gamification_game_condition_game_story_label") }}
          </OverflowMenuItem>
          <OverflowMenuItem
            v-if="!isGameContext || isAchievement"
            :disabled="isGameDisabled"
            @click="addCollectibleCondition"
          >
            {{ t("gamification_game_condition_collectible_label") }}
          </OverflowMenuItem>
          <OverflowMenuItem :disabled="isGameDisabled" @click="addChoiceCondition">
            {{ t("gamification_game_condition_game_branch_label") }}
          </OverflowMenuItem>
          <OverflowMenuItem :disabled="isGameDisabled" @click="addLevelCondition">
            {{ t("gamification_game_condition_level_label") }}
          </OverflowMenuItem>
          <OverflowMenuItem
            :disabled="hasConditionsPresent || isGameDisabled"
            @click="addQuizRankCondition"
          >
            {{ t("gamification_game_condition_quiz_rank_label") }}
          </OverflowMenuItem>
        </template>
      </overflow-menu-button>
    </template>
  </content-popup>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { isEmpty, isNull } from "@utils/var.check";
import { clickOutsideDirective } from "@utils/directives";
import ScrollUtils from "@utils/scroll.utils";
import ObjectUtils from "@utils/object.utils";
import StringUtils from "@utils/string.utils";
import appConfigs from "@configs/app";

import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant";
import {
  CONDITION_TARGET_TYPE,
  MAX_RULES,
  DEFAULT_MIN_RANK,
  DEFAULT_MAX_RANK,
  DEFAULT_LEVEL,
} from "@modules/rule-condition/service/rule.condition.constant";
import {
  EVENT,
  OPERATOR,
  PREREQUISITE_TYPE,
  CRITERION_VALUE,
  type Operator,
  type CriterionValue,
  ActivityType,
} from "@plugins/course-lesson-plan/course.constant.ts";
import { CONDITION_TYPE, GAME_AWARD_TYPE } from "@modules/editor/service/gamification.constant";

import SectionIcon from "@images/icons/indicative/32px/Section32.svg?component";
import PageIcon from "@images/icons/indicative/32px/Page32.svg?component";
import QuizPageIcon from "@images/icons/indicative/32px/QuizPage32.svg?component";

import GamificationConditionItem from "@modules/editor/components/gamification/GamificationConditionItem.vue";
import ContentPopup from "@plugins/common/components/ContentPopup.vue";
import DropdownComponent from "@plugins/common/components/DropdownComponent.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import InputRadio from "@plugins/common/components/InputRadio.vue";
import OverflowMenuButton from "@plugins/common/components/OverflowMenuButton.vue";
import OverflowMenuItem from "@plugins/common/components/OverflowMenuItem.vue";

import { useValidation } from "@utils/validation-composable.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { SaveButton } from "@plugins/common/types.ts";
import {
  ActivityItem,
  OperatorItem,
  PrerequisiteItem,
  PrerequisitesDropDownData,
  SectionItem,
  SelectedPrerequisite,
} from "@plugins/course-lesson-plan/types.ts";

const vClickOutside = clickOutsideDirective["click-outside"];

const props = withDefaults(
  defineProps<{
    currentSectionId?: number;
    currentActivityId?: number;
    prerequisitesDropDownData?: PrerequisitesDropDownData;
    isSectionPrerequisite?: boolean;
    lessonId?: number;
    existingPrerequisites?: PrerequisiteItem[];
    existingOperators?: OperatorItem[];
    referenceElement?: HTMLElement | null;
    context?: "LESSON_ACTIVITY" | "GAME_AWARD";
    gameAward?: { id: number; isNew?: boolean };
    gameData?: { enabled?: boolean } | undefined;
    isAchievement?: boolean;
    hideSectionActivityNumber?: boolean;
    placement?: string;
    isFromGameStory?: boolean;
    awardType?: "ACHIEVEMENT" | "GAME_AVATAR" | "GAME_STORY" | "COLLECTIBLE";
  }>(),
  {
    currentSectionId: 0,
    currentActivityId: 0,
    prerequisitesDropDownData: () => ({ sections: [] }),
    isSectionPrerequisite: false,
    lessonId: 0,
    existingPrerequisites: () => [],
    existingOperators: () => [],
    referenceElement: null,
    context: "LESSON_ACTIVITY",
    gameAward: () => ({ id: 0 }),
    gameData: () => ({ enabled: false }),
    isAchievement: false,
    hideSectionActivityNumber: false,
    placement: "bottom",
    isFromGameStory: false,
    awardType: "ACHIEVEMENT",
  },
);

const emit = defineEmits<(e: "event", payload: { type: string; data?: unknown }) => void>();

const { t } = useI18n();
const { validateNumberInput } = useValidation();
useErrorHandler();

const dirtyId = computed(() => {
  if (isGameContext.value) return `game_award_${props.gameAward?.id ?? 0}`;
  if (props.isSectionPrerequisite) return `lesson_plan_${props.currentSectionId ?? 0}`;
  return `lesson_plan_${props.currentActivityId ?? 0}`;
});

const { isDirty, setDirty, removeDirty, promptComponentDirty } = useDirty(dirtyId);

const visible = ref(false);
const loadingExistingPrerequisite = ref(false);
const userGuideUrl = appConfigs.addConditionUserGuideUrl;
const buttonState = ref<SaveButton>({
  kind: "primary",
  size: "small",
  loading: false,
  error: false,
});

const selectedPrerequisiteList = ref<SelectedPrerequisite[]>([]);
const selectedOperatorList = ref<Operator[]>([]);

const isGameContext = computed(() => props.context === CONDITION_TARGET_TYPE.GAME_AWARD);
const isGameAvatarAwardType = computed(() => props.awardType === GAME_AWARD_TYPE.GAME_AVATAR);

const title = computed(() => {
  if (isGameContext.value) {
    return props.gameAward && props.gameAward.isNew
      ? t("gamification_game_condition_add_condition_label")
      : t("gamification_game_condition_edit_condition_label");
  }
  return props.isSectionPrerequisite
    ? t("course_plan_resource_completion_prerequisite_content_popup_title")
    : t("lesson_plan_activity_prerequisite_content_popup_title");
});

const subtitle = computed(() => {
  if (isGameContext.value) {
    const achievementSubtitle = isGameAvatarAwardType.value
      ? t("gamification_game_condition_condition_game_avatar_message_label")
      : t("gamification_game_condition_condition_achievement_message_label");
    return props.isAchievement
      ? achievementSubtitle
      : t("gamification_game_condition_condition_game_story_message_label");
  }
  return props.isSectionPrerequisite
    ? t("lesson_plan_section_prerequisite_content_popup_subtitle")
    : t("lesson_plan_activity_prerequisite_content_popup_subtitle");
});

const filteredPrerequisitesDropDownData = computed<SectionItem[]>(() => {
  const cloned = ObjectUtils.deepClone(
    props.prerequisitesDropDownData,
  ) as PrerequisitesDropDownData;
  const result: SectionItem[] = [];
  if (props.currentSectionId) {
    if (props.isSectionPrerequisite) {
      return cloned.sections.filter((section) => section.id !== props.currentSectionId);
    } else {
      cloned.sections.forEach((section) => {
        if (section.id === props.currentSectionId && props.currentActivityId) {
          section.activityList = section.activityList.filter(
            (activity) => activity.id !== props.currentActivityId,
          );
        }
        result.push(section);
      });
      return result;
    }
  }
  if (!isEmpty(cloned)) {
    cloned.sections.forEach((section) => result.push(section));
  }
  return result;
});

const filteredSectionActivityList = (sectionId?: number) => {
  if (sectionId == null) return [] as ActivityItem[];
  return (
    filteredPrerequisitesDropDownData.value.find((s) => s.id === sectionId)?.activityList ?? []
  );
};

const activityQuizSelected = (sectionId?: number, activityId?: number) => {
  if (sectionId == null || activityId == null) return false;
  const list = filteredSectionActivityList(sectionId);
  if (list.length === 0) return false;
  return list.find((a) => a.id === activityId)?.type === LESSON_ACTIVITY_TYPE.ACTIVITY_QUIZ;
};

const sectionErrorMessage = (prerequisite: SelectedPrerequisite) =>
  prerequisite?.errorTypes.includes("SECTION") ? prerequisite.errorMessage : "";

const activityErrorMessage = (prerequisite: SelectedPrerequisite) =>
  prerequisite?.errorTypes.includes("ACTIVITY") ? "DUMMY_TEXT" : "";

const selectedActivity = (activityId?: number) => (isEmpty(activityId) ? null : String(activityId));

const sectionIconText = (sectionPageNo?: number) =>
  sectionPageNo ? `${StringUtils.getAlphabeticalLetter(sectionPageNo - 1)}` : "";

const activityLabelIcon = (type: ActivityItem["type"]) =>
  type === "ACTIVITY" ? PageIcon : QuizPageIcon;

const quizScoreDropdownValue = computed<CriterionValue>(() =>
  isGameContext.value ? CRITERION_VALUE.RESOURCE_SCORE : CRITERION_VALUE.QUIZ_SCORE,
);

const addButtonDisabled = computed(
  () =>
    selectedPrerequisiteList.value.length >= MAX_RULES ||
    hasLessonStartCondition.value ||
    hasQuizRankCondition.value,
);

const hasConditionsPresent = computed(() => !isEmpty(selectedPrerequisiteList.value));

const isActivityPrerequisiteType = (preRequisite: SelectedPrerequisite) =>
  preRequisite.type === PREREQUISITE_TYPE.ACTIVITY &&
  preRequisite.criterion !== PREREQUISITE_TYPE.GAME_AWARD &&
  preRequisite.criterion !== PREREQUISITE_TYPE.GAME_CHOICE &&
  preRequisite.criterion !== PREREQUISITE_TYPE.LEVEL_UP &&
  preRequisite.criterion !== PREREQUISITE_TYPE.QUIZ_RANK &&
  preRequisite.resourceCompletionPrerequisite !== -1;

const isQuizRankConditionType = (condition: SelectedPrerequisite) =>
  condition.criterion === PREREQUISITE_TYPE.QUIZ_RANK;

const hasLessonStartCondition = computed(() =>
  selectedPrerequisiteList.value.some(
    (s) =>
      s.resourceCompletionPrerequisite === -1 && s.criterion === CRITERION_VALUE.RESOURCE_START,
  ),
);

const hasQuizRankCondition = computed(() =>
  selectedPrerequisiteList.value.some((s) => s.criterion === PREREQUISITE_TYPE.QUIZ_RANK),
);

const addPrerequisiteButtonLabel = computed(() =>
  isGameContext.value
    ? t("gamification_game_condition_add_course_button_label")
    : t("add_resource_prerequisite_button_label"),
);

const isGameDisabled = computed(() => isNull(props.gameData) || !props.gameData?.enabled);

const sectionPrerequisitesSelectedObject = (selectedPrerequisite: SelectedPrerequisite) =>
  filteredPrerequisitesDropDownData.value.find(
    (item) => item.id.toString() === selectedPrerequisite?.sectionId?.toString(),
  );

const activityPrerequisitesSelectedObject = (selectedPrerequisite: SelectedPrerequisite) =>
  filteredSectionActivityList(selectedPrerequisite?.sectionId).find(
    (item) => item.id.toString() === (selectedActivity(selectedPrerequisite?.activityId) ?? ""),
  );

function anchorToCurrentSectionDropdownItem(index: number) {
  const parentSelector = document.querySelector(
    `.section-prerequisite-dropdown-${index}`,
  ) as HTMLElement | null;
  const childSelectors = parentSelector?.querySelectorAll(".cv-dropdown-item") ?? [];

  if (!props.currentSectionId) return;

  const currentSectionId = props.currentSectionId.toString();
  let currentSelector: Element | undefined;

  for (const selector of childSelectors) {
    // @ts-expect-error vue instance on element
    if (selector.__vue__?.value === currentSectionId) {
      currentSelector = selector;
      break;
    }
  }

  nextTick(() => {
    if (!currentSelector) return;
    ScrollUtils.scrollToView(currentSelector as HTMLElement, {
      behavior: "smooth",
      block: "center",
    });
  });
}

function addLessonStartCondition() {
  if (selectedPrerequisiteList.value.length > 0) return;
  setDirty(dirtyId.value);
  selectedPrerequisiteList.value.push({
    type: PREREQUISITE_TYPE.ACTIVITY,
    criterion: CRITERION_VALUE.RESOURCE_START,
    resourceCompletionPrerequisite: -1,
    errorMap: { level: "", gameAward: "" },
    errorMessage: "",
    errorTypes: [],
  });
}

function handleAddNewPrerequisite(
  defaultCriterion: CriterionValue,
  defaultType: SelectedPrerequisite["type"],
  gameAwardType?: SelectedPrerequisite["gameAwardType"],
) {
  setDirty(dirtyId.value);
  addNewPrerequisite(defaultCriterion, defaultType, gameAwardType);
  if (selectedPrerequisiteList.value.length > 1) {
    selectedOperatorList.value.push(OPERATOR.AND);
  }
}

function addLessonProgressCondition() {
  handleAddNewPrerequisite(CRITERION_VALUE.RESOURCE_COMPLETION, PREREQUISITE_TYPE.ACTIVITY);
}

function addChoiceCondition() {
  const defaultCriterion = PREREQUISITE_TYPE.GAME_CHOICE as CriterionValue;
  const defaultType = isGameContext.value
    ? PREREQUISITE_TYPE.GAME_CHOICE
    : PREREQUISITE_TYPE.ACTIVITY;
  handleAddNewPrerequisite(defaultCriterion, defaultType);
}

function addGameStoryCondition() {
  const defaultCriterion = PREREQUISITE_TYPE.GAME_AWARD as CriterionValue;
  const defaultType = isGameContext.value
    ? PREREQUISITE_TYPE.GAME_AWARD
    : PREREQUISITE_TYPE.ACTIVITY;
  handleAddNewPrerequisite(defaultCriterion, defaultType, GAME_AWARD_TYPE.GAME_STORY);
}

function addCollectibleCondition() {
  const defaultCriterion = PREREQUISITE_TYPE.GAME_AWARD as CriterionValue;
  const defaultType = isGameContext.value
    ? PREREQUISITE_TYPE.GAME_AWARD
    : PREREQUISITE_TYPE.ACTIVITY;
  handleAddNewPrerequisite(defaultCriterion, defaultType, GAME_AWARD_TYPE.COLLECTIBLE);
}

function addLevelCondition() {
  const defaultCriterion = PREREQUISITE_TYPE.LEVEL_UP as CriterionValue;
  const defaultType = isGameContext.value
    ? PREREQUISITE_TYPE.GAME_AWARD
    : PREREQUISITE_TYPE.ACTIVITY;
  handleAddNewPrerequisite(defaultCriterion, defaultType);
}

function addQuizRankCondition() {
  const defaultCriterion = PREREQUISITE_TYPE.QUIZ_RANK as CriterionValue;
  const defaultType = isGameContext.value
    ? PREREQUISITE_TYPE.GAME_AWARD
    : PREREQUISITE_TYPE.ACTIVITY;
  handleAddNewPrerequisite(defaultCriterion, defaultType);
}

function addNewPrerequisite(
  criterion: CriterionValue,
  type: SelectedPrerequisite["type"],
  gameAwardType?: SelectedPrerequisite["gameAwardType"],
) {
  selectedPrerequisiteList.value.push({
    sectionId: undefined,
    activityId: undefined,
    criterion,
    errorTypes: [],
    errorMessage: "",
    errorMap: { level: "", gameAward: "", gameChoice: "", section: "", activity: "" },
    type,
    gameAwardType,
    gameAwardPrerequisite: undefined,
    gameChoice: undefined,
    levelPrerequisite: DEFAULT_LEVEL,
    minRankPosition: DEFAULT_MIN_RANK,
    maxRankPosition: DEFAULT_MAX_RANK,
  });
}

function deletePrerequisite(prerequisiteIndex: number) {
  setDirty(dirtyId.value);
  selectedPrerequisiteList.value.splice(prerequisiteIndex, 1);

  if (isEmpty(selectedOperatorList.value[prerequisiteIndex])) {
    selectedOperatorList.value.pop();
    return;
  }

  if (selectedOperatorList.value[prerequisiteIndex] === OPERATOR.OR && prerequisiteIndex !== 0) {
    if (selectedOperatorList.value[prerequisiteIndex - 1] === OPERATOR.OR) {
      selectedOperatorList.value.splice(prerequisiteIndex, 1);
      return;
    } else {
      selectedOperatorList.value.splice(prerequisiteIndex - 1, 1);
      return;
    }
  }

  selectedOperatorList.value.splice(prerequisiteIndex, 1);
}

function handleConfirm(btnState: SaveButton) {
  if (!isDirty(dirtyId.value)) {
    handleClose();
    return;
  }
  buttonState.value = btnState;
  if (validateSelectedPrerequisiteList()) {
    buttonState.value.loading = true;
    removeDirty(dirtyId.value);
    visible.value = false;
    createPrerequisites();
  }
}

function handleClose() {
  promptComponentDirty().then((response) => {
    if (response) {
      emit("event", {
        type: EVENT.TOGGLE_RESOURCE_COMPLETION_PREREQUISITE_CONTENT_POPUP,
        data: {
          id: props.isSectionPrerequisite ? props.currentSectionId : props.currentActivityId,
          visible: false,
        },
      });
      visible.value = false;
    }
  });
}

function handleClickOutside() {
  handleClose();
}

function handleChangeOperator(index: number, value: Operator) {
  setDirty(dirtyId.value);
  selectedOperatorList.value[index] = value;
}

function handleChangeGamificationConditionItem(
  index: number,
  type: CriterionValue,
  value: Record<string, number>,
) {
  setDirty(dirtyId.value);
  if (type === PREREQUISITE_TYPE.LEVEL_UP) {
    selectedPrerequisiteList.value[index].levelPrerequisite = value.level;
  } else if (type === PREREQUISITE_TYPE.GAME_AWARD) {
    selectedPrerequisiteList.value[index].gameAwardPrerequisite = value.id;
  } else if (type === PREREQUISITE_TYPE.GAME_CHOICE) {
    selectedPrerequisiteList.value[index].gameChoice = value.id;
    selectedPrerequisiteList.value[index].gameAwardId = value.gameAwardId;
  } else if (type === PREREQUISITE_TYPE.QUIZ_RANK) {
    selectedPrerequisiteList.value[index].sectionId = value.sectionId;
    selectedPrerequisiteList.value[index].activityId = value.activityId;
    selectedPrerequisiteList.value[index].minRankPosition = value.minRankPosition;
    selectedPrerequisiteList.value[index].maxRankPosition = value.maxRankPosition;
  }
}

function handleChangeSectionPrerequisite(index: number, value: string | number | null) {
  if (selectedPrerequisiteList.value[index].sectionId != null) {
    resetActivityAndCriterion(index);
  }
  setDirty(dirtyId.value);
  selectedPrerequisiteList.value[index].sectionId = parseInt(String(value));
}

function handleChangeActivityPrerequisite(
  index: number,
  sectionId: number | undefined,
  value: string | number | null,
) {
  if (value == null) return;
  setDirty(dirtyId.value);
  const activityId = parseInt(String(value));
  selectedPrerequisiteList.value[index].activityId = activityId;

  const currentCriterion = selectedPrerequisiteList.value[index].criterion;
  if (
    currentCriterion === CRITERION_VALUE.QUIZ_SCORE ||
    currentCriterion === CRITERION_VALUE.RESOURCE_SCORE
  ) {
    if (!activityQuizSelected(sectionId, activityId)) {
      handleChangeCriterionPrerequisite(index, CRITERION_VALUE.RESOURCE_COMPLETION);
    }
  }
}

function handleChangeCriterionPrerequisite(index: number, criterion: CriterionValue) {
  setDirty(dirtyId.value);
  selectedPrerequisiteList.value[index].criterion = criterion;

  if (criterion === CRITERION_VALUE.QUIZ_SCORE || criterion === CRITERION_VALUE.RESOURCE_SCORE) {
    resetQuizScoreFromAndTo(index);
  } else if (selectedPrerequisiteList.value[index]?.quizScoreFrom != null) {
    removeQuizScoreFromAndTo(index);
  }
}

function resetActivityAndCriterion(index: number) {
  selectedPrerequisiteList.value[index].activityId = undefined;

  if (selectedPrerequisiteList.value[index].type === PREREQUISITE_TYPE.ACTIVITY) {
    handleChangeCriterionPrerequisite(index, CRITERION_VALUE.RESOURCE_COMPLETION);
  } else if (selectedPrerequisiteList.value[index].type === PREREQUISITE_TYPE.QUIZ_RANK) {
    resetQuizRankFromAndTo(index);
  }
}

function resetQuizRankFromAndTo(index: number) {
  selectedPrerequisiteList.value[index].minRankPosition = undefined;
  selectedPrerequisiteList.value[index].maxRankPosition = undefined;
}

function resetQuizScoreFromAndTo(index: number) {
  selectedPrerequisiteList.value[index].quizScoreFrom = 50;
  selectedPrerequisiteList.value[index].previousQuizScoreFrom = 50;
  selectedPrerequisiteList.value[index].quizScoreTo = 100;
  selectedPrerequisiteList.value[index].previousQuizScoreTo = 100;
}

function removeQuizScoreFromAndTo(index: number) {
  delete selectedPrerequisiteList.value[index].quizScoreFrom;
  delete selectedPrerequisiteList.value[index].previousQuizScoreFrom;
  delete selectedPrerequisiteList.value[index].quizScoreTo;
  delete selectedPrerequisiteList.value[index].previousQuizScoreTo;
}

function handleInputQuizScoreFrom(index: number, value: number) {
  if (validateQuizScoreFrom(index, value)) {
    if (
      value === selectedPrerequisiteList.value[index].quizScoreTo &&
      (selectedPrerequisiteList.value[index].quizScoreFrom ?? 0) + 1 === value
    ) {
      revertQuizScoreFrom(index);
      return;
    }
  }

  if ((selectedPrerequisiteList.value[index].quizScoreFrom ?? 0) + 1 === value) {
    setDirty(dirtyId.value);
    selectedPrerequisiteList.value[index].quizScoreFrom = parseInt(String(value));
    selectedPrerequisiteList.value[index].previousQuizScoreFrom = parseInt(String(value));
  }
}

function handleChangeQuizScoreFrom(index: number, event: Event) {
  const value = (event.target as HTMLInputElement).valueAsNumber;
  if (validateQuizScoreFrom(index, value)) {
    revertQuizScoreFrom(index);
    return;
  }
  setDirty(dirtyId.value);
  selectedPrerequisiteList.value[index].quizScoreFrom = parseInt(String(value));
  selectedPrerequisiteList.value[index].previousQuizScoreFrom = parseInt(String(value));
}

function validateQuizScoreFrom(index: number, value: number) {
  if (!validateNumberInput(value, { min: 0, max: 100 })) return true;
  return (selectedPrerequisiteList.value[index].quizScoreTo ?? 0) <= value;
}

function revertQuizScoreFrom(index: number) {
  selectedPrerequisiteList.value[index].quizScoreFrom = -1;
  nextTick(() => {
    selectedPrerequisiteList.value[index].quizScoreFrom =
      selectedPrerequisiteList.value[index].previousQuizScoreFrom;
  });
}

function handleInputQuizScoreTo(index: number, value: number) {
  if (validateQuizScoreTo(index, value)) {
    if (
      value === (selectedPrerequisiteList.value[index].quizScoreFrom ?? 0) &&
      (selectedPrerequisiteList.value[index].quizScoreTo ?? 0) - 1 === value
    ) {
      revertQuizScoreTo(index);
    }
    return;
  }
  setDirty(dirtyId.value);
  selectedPrerequisiteList.value[index].quizScoreTo = parseInt(String(value));
  selectedPrerequisiteList.value[index].previousQuizScoreTo = parseInt(String(value));
}

function handleChangeQuizScoreTo(index: number, event: Event) {
  const value = (event.target as HTMLInputElement).valueAsNumber;
  if (validateQuizScoreTo(index, value)) {
    revertQuizScoreTo(index);
    return;
  }
  setDirty(dirtyId.value);
  selectedPrerequisiteList.value[index].quizScoreTo = parseInt(String(value));
  selectedPrerequisiteList.value[index].previousQuizScoreTo = parseInt(String(value));
}

function validateQuizScoreTo(index: number, value: number) {
  if (!validateNumberInput(value, { min: 1, max: 100 })) return true;
  return (selectedPrerequisiteList.value[index].quizScoreFrom ?? 0) >= value;
}

function revertQuizScoreTo(index: number) {
  selectedPrerequisiteList.value[index].quizScoreTo = -1;
  nextTick(() => {
    selectedPrerequisiteList.value[index].quizScoreTo =
      selectedPrerequisiteList.value[index].previousQuizScoreTo;
  });
}

function createPrerequisites() {
  const model = populatePrerequisitesData();
  emit("event", {
    type: EVENT.SAVE_RULE_CONDITION_RESOURCE_PREREQUISITE,
    data: model,
  });
}

function populatePrerequisitesData() {
  const model = populateDefaultRuleConditionSaveApiModel();

  if (selectedPrerequisiteList.value.length === 1) {
    return populateSinglePrerequisite(model);
  }
  return populateMultiplePrerequisite(model);
}

function populateDefaultRuleConditionSaveApiModel() {
  let base: Record<string, unknown> = {
    lessonId: props.lessonId,
    conditionTargetId:
      props.context === CONDITION_TARGET_TYPE.GAME_AWARD
        ? props.gameAward?.id
        : props.isSectionPrerequisite
          ? props.currentSectionId
          : props.currentActivityId,
    conditionTargetType: props.context,
  };

  if (isGameContext.value) {
    base = {
      ...base,
      gameConditionGroupApiModel: {
        operator: OPERATOR.AND,
        gameConditionGroupApiModels: [] as unknown[],
        gameConditionApiModels: [] as unknown[],
      },
    };
  } else {
    base = {
      ...base,
      ruleConditionGroupApiModel: {
        operator: OPERATOR.AND,
        ruleConditionGroupApiModels: [] as unknown[],
        ruleConditionApiModels: [] as unknown[],
      },
    };
  }
  return base;
}

function populateSinglePrerequisite(model: Record<string, unknown>) {
  const only = selectedPrerequisiteList.value[0];
  const groupKey = isGameContext.value
    ? "gameConditionGroupApiModel"
    : "ruleConditionGroupApiModel";
  const apiKey = isGameContext.value ? "gameConditionApiModels" : "ruleConditionApiModels";

  if (hasLessonStartCondition.value) {
    (model[groupKey] as Record<string, unknown>)[apiKey] = [
      {
        ruleConditionType: CRITERION_VALUE.RESOURCE_START,
        gameConditionType: CRITERION_VALUE.RESOURCE_START,
        resourceCompletionPrerequisite: 0,
      },
    ];
  } else {
    (model[groupKey] as Record<string, unknown>)[apiKey] = [
      {
        ruleConditionType: only.criterion,
        gameConditionType: only.criterion,
        resourceCompletionPrerequisite: only.activityId ?? only.sectionId,
        groupIndexNo: null,
        minPercentage: only.quizScoreFrom ?? null,
        maxPercentage: only.quizScoreTo ?? null,
        levelPrerequisite: only.levelPrerequisite,
        gameAwardPrerequisite: only.gameAwardPrerequisite,
        gameChoice: only.gameChoice,
        minRankPosition: only.minRankPosition,
        maxRankPosition: only.maxRankPosition,
      },
    ];
  }
  return model;
}

function populateMultiplePrerequisite(model: Record<string, unknown>) {
  const groupKey = isGameContext.value
    ? "gameConditionGroupApiModel"
    : "ruleConditionGroupApiModel";
  const groupsKey = isGameContext.value
    ? "gameConditionGroupApiModels"
    : "ruleConditionGroupApiModels";
  const apiKey = isGameContext.value ? "gameConditionApiModels" : "ruleConditionApiModels";

  if (hasLessonStartCondition.value || hasQuizRankCondition.value) {
    return model;
  }

  const ruleConditions: Record<string, unknown>[] = [];
  const ruleConditionGroups: Record<string, unknown>[] = [];

  let onlyOrOperators = true;
  let onlyAndOperators = true;
  let hasAnyOrOperators = false;
  let continueGroupCreation = false;

  let currentGroupIndexNo = 0;
  const lastOperatorIndex = selectedOperatorList.value.length - 1;

  const pushRule = (p: SelectedPrerequisite, isAnd: boolean) => {
    ruleConditions.push({
      ruleConditionType: p.criterion,
      gameConditionType: p.criterion,
      resourceCompletionPrerequisite: p.activityId ?? p.sectionId,
      groupIndexNo: isAnd || continueGroupCreation ? currentGroupIndexNo : null,
      minPercentage: p.quizScoreFrom ?? null,
      maxPercentage: p.quizScoreTo ?? null,
      levelPrerequisite: p.levelPrerequisite,
      gameAwardPrerequisite: p.gameAwardPrerequisite,
      gameChoice: p.gameChoice,
      minRankPosition: p.minRankPosition,
      maxRankPosition: p.maxRankPosition,
    });
  };

  selectedOperatorList.value.forEach((op, index) => {
    const isAnd = op === OPERATOR.AND;

    pushRule(selectedPrerequisiteList.value[index], isAnd);

    if (isAnd) {
      onlyOrOperators = false;
      if (!continueGroupCreation) {
        ruleConditionGroups.push({ operator: OPERATOR.AND });
      }
    } else {
      hasAnyOrOperators = true;
      onlyAndOperators = false;
      if (continueGroupCreation) currentGroupIndexNo++;
    }

    continueGroupCreation = isAnd;

    if (index === lastOperatorIndex) {
      pushRule(selectedPrerequisiteList.value[index + 1], isAnd);
    }
  });

  const onlySingleType = onlyOrOperators || onlyAndOperators;
  if (onlySingleType) {
    ruleConditions.forEach((rc) => {
      (rc as { groupIndexNo: number | null }).groupIndexNo = null;
    });
  }

  if (hasAnyOrOperators) {
    (model[groupKey] as { operator: Operator }).operator = OPERATOR.OR;
  }

  (model[groupKey] as Record<string, unknown>)[apiKey] = ruleConditions;

  if (!onlySingleType) {
    (model[groupKey] as Record<string, unknown>)[groupsKey] = ruleConditionGroups;
  }

  return model;
}

function validateCriterion(criterion: CriterionValue) {
  return (
    criterion === CRITERION_VALUE.RESOURCE_COMPLETION ||
    criterion === CRITERION_VALUE.QUIZ_SCORE ||
    criterion === CRITERION_VALUE.RESOURCE_SCORE ||
    criterion === CRITERION_VALUE.RESOURCE_START
  );
}

function validateSelectedPrerequisiteList() {
  if (selectedPrerequisiteList.value.length > MAX_RULES) return false;

  if (
    (hasQuizRankCondition.value || hasLessonStartCondition.value) &&
    selectedPrerequisiteList.value.length !== 1
  ) {
    return false;
  }

  let isValid = true;

  selectedPrerequisiteList.value.forEach((selectedPrerequisite, index) => {
    const sectionId = selectedPrerequisite.sectionId;
    const activityId = selectedPrerequisite.activityId;
    const criterion = selectedPrerequisite.criterion;

    if (isActivityPrerequisiteType(selectedPrerequisite) && sectionId == null) {
      isValid = false;
      selectedPrerequisiteList.value[index].errorTypes = ["SECTION"];
      selectedPrerequisiteList.value[index].errorMessage = t(
        "lesson_plan_activity_prerequisite_content_popup_field_empty_error_message",
      ) as string;
      return;
    }

    if (
      isActivityPrerequisiteType(selectedPrerequisite) &&
      sectionId === props.currentSectionId &&
      activityId == null
    ) {
      isValid = false;
      selectedPrerequisiteList.value[index].errorTypes = ["SECTION", "ACTIVITY"];
      selectedPrerequisiteList.value[index].errorMessage = t(
        "lesson_plan_activity_prerequisite_content_popup_activity_field_empty_error_message",
      ) as string;
      return;
    }

    selectedPrerequisiteList.value[index].errorTypes = [];
    selectedPrerequisiteList.value[index].errorMessage = "";
    selectedPrerequisiteList.value[index].errorMap.section = "";
    selectedPrerequisiteList.value[index].errorMap.activity = "";

    if (isQuizRankConditionType(selectedPrerequisite) && sectionId == null) {
      isValid = false;
      selectedPrerequisite.errorMap.section = t(
        "lesson_plan_activity_prerequisite_content_popup_field_empty_error_message",
      ) as string;
      return;
    }

    selectedPrerequisiteList.value[index].errorMap.activity = "";
    if (isQuizRankConditionType(selectedPrerequisite) && sectionId != null && activityId == null) {
      isValid = false;
      selectedPrerequisite.errorMap.activity = t(
        "lesson_plan_activity_prerequisite_content_popup_field_empty_error_message",
      ) as string;
      return;
    }

    if (
      isActivityPrerequisiteType(selectedPrerequisite) &&
      !validateCriterion(selectedPrerequisite.criterion)
    ) {
      isValid = false;
    }

    if (
      isActivityPrerequisiteType(selectedPrerequisite) &&
      !activityQuizSelected(sectionId, activityId) &&
      (selectedPrerequisite.criterion === CRITERION_VALUE.QUIZ_SCORE ||
        selectedPrerequisite.criterion === CRITERION_VALUE.RESOURCE_SCORE)
    ) {
      isValid = false;
    }

    if (
      isActivityPrerequisiteType(selectedPrerequisite) &&
      selectedPrerequisite.criterion === CRITERION_VALUE.QUIZ_SCORE
    ) {
      if (
        !validateNumberInput(selectedPrerequisite.quizScoreFrom ?? NaN, { min: 0, max: 100 }) ||
        !validateNumberInput(selectedPrerequisite.quizScoreTo ?? NaN, { min: 1, max: 100 })
      ) {
        isValid = false;
      }
      if ((selectedPrerequisite.quizScoreFrom ?? 0) >= (selectedPrerequisite.quizScoreTo ?? 0)) {
        isValid = false;
      }
    }

    if (isQuizRankConditionType(selectedPrerequisite)) {
      if (
        !validateNumberInput(selectedPrerequisite.minRankPosition ?? NaN, { min: 1, max: 100 }) ||
        !validateNumberInput(selectedPrerequisite.maxRankPosition ?? NaN, { min: 1, max: 100 })
      ) {
        isValid = false;
      }
      if (
        (selectedPrerequisite.minRankPosition ?? 0) > (selectedPrerequisite.maxRankPosition ?? 0)
      ) {
        isValid = false;
      }
    }

    if (criterion === PREREQUISITE_TYPE.GAME_AWARD) {
      if (isNull(selectedPrerequisite.gameAwardPrerequisite)) {
        selectedPrerequisite.errorMap.gameAward = t(
          "gamification_game_condition_field_empty_error_message",
        ) as string;
        isValid = false;
      } else {
        selectedPrerequisite.errorMap.gameAward = "";
      }
    }

    if (criterion === PREREQUISITE_TYPE.GAME_CHOICE) {
      if (isNull(selectedPrerequisite.gameChoice)) {
        selectedPrerequisite.errorMap.gameChoice = t(
          "gamification_game_condition_field_empty_error_message",
        ) as string;
        isValid = false;
      } else {
        selectedPrerequisite.errorMap.gameChoice = "";
      }

      if (isNull(selectedPrerequisite.gameAwardId)) {
        selectedPrerequisite.errorMap.gameAward = t(
          "gamification_game_condition_field_empty_error_message",
        ) as string;
        isValid = false;
      } else {
        selectedPrerequisite.errorMap.gameAward = "";
      }
    }

    if (criterion === PREREQUISITE_TYPE.LEVEL_UP) {
      if (
        selectedPrerequisite.levelPrerequisite == null ||
        !validateNumberInput(selectedPrerequisite.levelPrerequisite, { min: 0, max: 99 })
      ) {
        selectedPrerequisite.errorMap.level = t(
          "gamification_game_condition_field_invalid_number_error_message",
        ) as string;
        isValid = false;
      } else {
        selectedPrerequisite.errorMap.level = "";
      }
    }
  });

  return isValid;
}

function populateExistingPrerequisitesAndOperators() {
  if ((props.existingPrerequisites?.length ?? 0) > 0) {
    const operators = (props.existingOperators ?? []).reduce<Record<number, Operator>>(
      (acc, op) => {
        if (acc[op.ruleConditionGroupId] === undefined) {
          acc[op.ruleConditionGroupId] = op.operator;
        }
        return acc;
      },
      {},
    );

    const prerequisites: SelectedPrerequisite[] = [];

    props.existingPrerequisites?.forEach((p, index) => {
      // populate for selected prerequisite list
      const {
        sectionId,
        activityId,
        criterion,
        type,
        levelPrerequisite,
        gameAwardPrerequisite,
        gameChoice,
        gameAwardType,
        resourceCompletionPrerequisite,
        minRankPosition,
        maxRankPosition,
      } = p;

      const temp: SelectedPrerequisite = {
        sectionId,
        activityId,
        criterion,
        levelPrerequisite,
        gameAwardPrerequisite,
        gameChoice,
        gameAwardType,
        minRankPosition,
        maxRankPosition,
        resourceCompletionPrerequisite:
          type === PREREQUISITE_TYPE.ACTIVITY &&
          criterion === (CONDITION_TYPE.RESOURCE_STARTS as unknown as CriterionValue) &&
          resourceCompletionPrerequisite === 0
            ? -1
            : resourceCompletionPrerequisite,
        errorTypes: [],
        errorMessage: "",
        errorMap: { level: "", gameAward: "" },
        type: isGameContext.value ? type : PREREQUISITE_TYPE.ACTIVITY,
        ...((criterion === CRITERION_VALUE.QUIZ_SCORE ||
          criterion === CRITERION_VALUE.RESOURCE_SCORE) && {
          previousQuizScoreFrom: p.quizScoreFrom,
          quizScoreFrom: p.quizScoreFrom,
          previousQuizScoreTo: p.quizScoreTo,
          quizScoreTo: p.quizScoreTo,
        }),
      };

      prerequisites.push(temp);

      const nextIndex = index + 1;
      if (nextIndex < (props.existingPrerequisites?.length ?? 0)) {
        const currentParentId = p.parentId ?? -1;
        const currentOp = operators[currentParentId];

        const nextP = props.existingPrerequisites?.[nextIndex];
        if (nextP && nextP.parentId != null && currentParentId === nextP.parentId) {
          selectedOperatorList.value.push(currentOp);
        } else {
          selectedOperatorList.value.push(OPERATOR.OR);
        }
      }
    });

    if (prerequisites.length > 0) {
      selectedPrerequisiteList.value = prerequisites;
      loadingExistingPrerequisite.value =
        !isGameContext.value ||
        selectedPrerequisiteList.value.some((pr) => isActivityPrerequisiteType(pr));
    }
  }
}

onMounted(() => {
  nextTick(() => {
    populateExistingPrerequisitesAndOperators();
    visible.value = true;
  });
});

onBeforeUnmount(() => {
  if (visible.value) {
    removeDirty(dirtyId.value);
    handleClose();
  }
});
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.content-popup-container.resource-prerequisite-popup {
  // to have a gap at the bottom of content subpage
  .content-popup {
    padding-bottom: $spacing-05;
  }

  .cv-number-input {
    .bx--number__input-wrapper {
      > input {
        min-width: 5rem;

        @include media("mobile") {
          min-width: 4.25rem;
          padding-right: $spacing-05;
        }
      }
    }
  }

  .content-popup-content {
    > button {
      display: flex;
      margin: $spacing-05 auto 0;
    }

    > .info {
      @include carbon--type-style("body-short-01", true);
      margin-bottom: $spacing-05;
      color: $text-02;
      .bx--link {
        @include carbon--type-style("body-short-01", true);
      }
    }

    .resource-prerequisite-list {
      .field-input {
        display: flex;
        flex-flow: row wrap;

        > div {
          width: 100%;
          padding: $spacing-05;
          background-color: $ui-background;
          border-radius: 10px;

          &.prerequisite-selection {
            &.or-selected {
              background-color: $ui-01;
              padding: 0 $spacing-05;
            }
          }
        }
      }
    }

    .resource-prerequisite-item {
      display: grid;

      grid-gap: $spacing-03;
      padding: $spacing-03;
      background-color: $ui-01;
      border-radius: 10px;

      @include media("tablet-min") {
        grid-template-columns: calc((100% - 4rem) / 2) calc((100% - 4rem) / 2) 3rem;
        grid-template-rows: auto 1fr;
      }

      &.gamification-condition-quiz-rank-item {
        .criterion-selection {
          .cv-number-input {
            padding-right: 0;
            .bx--number__input-wrapper {
              &::after {
                display: none;
              }
            }
          }
        }
      }
      .criterion-selection {
        display: flex;
        flex-flow: row wrap;
        align-items: center;

        @include media("tablet-max") {
          > div {
            &:nth-child(2) {
              flex: 0 0 100%;
              margin: $spacing-03 0;
            }
          }
        }

        @include media("tablet-min") {
          grid-column: 1 / 3;
          grid-row: 2;
        }

        .criterion-dropdown {
          // To prevent the dropdown from growing/shrinking when the option is changed
          max-width: 12rem;
        }

        > div {
          &:not(:last-child) {
            margin-right: $spacing-03;
          }
        }

        .cv-number-input {
          flex: 0;
          padding-right: $spacing-05;

          .bx--number__input-wrapper {
            &::after {
              content: "%";
              position: absolute;
              right: -1rem;
            }
            > input {
              background-color: $ui-background;
            }
          }
        }
      }

      label {
        display: inline-flex;
        position: relative;
        margin-right: $spacing-02;

        &::before {
          @include carbon--type-style("helper-text-01");
          content: attr(data-icon-text);
          position: absolute;
          top: 10px;
          width: 100%;
          color: $interactive-01;
          text-align: center;
        }

        &.section-label {
          &::before {
            color: $ui-01;
          }
        }
      }
    }
  }

  .game-condition-item {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    gap: 0.5rem;
    padding: $spacing-03;
    background-color: $ui-01;
    border-radius: 10px;

    &.choice {
      > .bx--form-item {
        margin-right: 0;

        @include media("tablet-min") {
          max-width: 16.5rem;
        }
      }
    }
    @include media("mobile") {
      // Prevent overlap of elements with delete button
      padding-right: $spacing-08;
    }

    .bx--number {
      .bx--number__input-wrapper {
        > input {
          background-color: $field-02;
        }
      }
    }

    .bx--form-item {
      .bx--dropdown,
      .bx--dropdown-item {
        .icon {
          max-height: 32px;
        }
      }
    }
  }

  .resource-prerequisite-item-wrapper {
    position: relative;

    [class*="btn-delete"] {
      @include media("mobile") {
        position: absolute;
        top: 1.25rem;
        right: 1rem;
      }

      @include media("tablet-min") {
        grid-column: 3;
        grid-row: 1 / 3;
        align-self: center;
      }
    }
  }

  .prerequisite-selection {
    &.and-selected {
      margin: (-$spacing-05) 0;
    }

    .input-radio {
      &:not(:first-of-type) {
        margin-left: $spacing-03;
      }

      > label {
        .text {
          margin-top: 0;
        }
      }
    }
  }

  .prerequisite-dropdown {
    .ellipsis-text {
      @include carbon--type-style("productive-heading-02");
      @include line-clamp(1);
      color: $text-02;
    }
  }

  .activity-prerequisite-dropdown {
    // Hide dummy text for error state, only required for the red border
    .bx--form-requirement {
      display: none;
    }
  }

  .section-prerequisite-dropdown-container {
    @include media("mobile") {
      // Cater space for the delete button in Mobile view
      max-width: calc(100% - 3rem);
    }

    // Error message
    .bx--form-requirement {
      width: 200%;
      min-height: 2.5rem;
      margin-top: 0;
      padding-top: 0;
    }
  }
}
</style>
